import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

import { eventJournalApi } from '@entities/eventJournal/api';
import { useGetAllQueryParams } from '@entities/eventJournal/lib/hooks';
import { currentEventId } from '@entities/eventJournal/model/queries';
import { handleStatusError } from '@shared/helpers/handleStatusError';
import { useNuqsQueryState } from '@shared/hooks';

export const useDeleteJournalEvent = ({
    onSuccessCb,
    onSettledCb,
}: {
    onSuccessCb?: () => void;
    onSettledCb?: () => void;
}) => {
    const queryClient = useQueryClient();
    const { adaptedQueryParams } = useGetAllQueryParams();
    const { t } = useTranslation();

    const { state: currentEventIdQueryState } = useNuqsQueryState(currentEventId);

    const deleteEventMutation = useMutation({
        mutationFn: eventJournalApi.deleteJournalEvent,
        onSettled: async () => {
            await queryClient.invalidateQueries({
                queryKey: eventJournalApi.getJournalEventsListQueryOptions({
                    queryParams: adaptedQueryParams,
                }).queryKey,
            });
            onSettledCb?.();
        },
        onSuccess: () => {
            onSuccessCb?.();
        },
        onError: (error: AxiosError<{ code: number; message: string; status: string }>) => {
            console.error('error in create event: ', error);
            if (error?.status === 500) {
                handleStatusError(error?.message, t('button.got_it.text'));
            }
            if (error?.response?.data?.status === 'error') {
                handleStatusError(error?.response?.data, t('button.got_it.text'));
            }
        },
    });

    const handleDelete = async () => {
        await deleteEventMutation.mutateAsync(Number(currentEventIdQueryState));
    };

    return {
        handleDelete,
        isPending: deleteEventMutation.isPending,
    };
};
